import { Skeleton } from '@/components/ui';
import { groupByMarket } from '@/lib/notifications/group-by-market';
import { groupByRecency } from '@/lib/notifications/group-by-recency';
import { useNotificationActions } from '@/lib/notifications/useNotificationActions';
import { useNotificationsList } from '@/lib/endpoints';
import type { NotificationResponseDto } from '@/lib/models';
import type { NotificationsListParamsExt } from '@/lib/notifications/list-params';
import type { NotificationTab } from '@/lib/notifications/notification-types';
import { useOpenNotificationDetail } from '@/store/notificationsStore';
import { NotificationItem } from './NotificationItem';
import { NotificationThread } from './NotificationThread';

export interface NotificationListProps {
  /** Active filter tab — feeds the server-side query. */
  tab: NotificationTab;
  /** Free-text search — feeds the server-side query. */
  q?: string;
  /** ISO date lower bound (inclusive) — feeds the server-side query. */
  dateFrom?: string;
  /** ISO date upper bound (inclusive) — feeds the server-side query. */
  dateTo?: string;
  /** Called after a row is clicked — lets the surrounding surface react (e.g. the desktop popover closes). */
  onItemClick?: () => void;
}

/**
 * The grouped notification feed: fetches a tab/search-filtered page from the
 * API, splits it into recency buckets (Today / Yesterday / Last 7 Days / Older),
 * and renders each bucket newest-first. Within a bucket, rows tied to the same
 * market thread under one card ({@link NotificationThread}) so related
 * lifecycle events read as one story. Clicking a row opens its detail pop-up
 * and marks it read (optimistically). Skeletons while loading; per-tab empty state.
 */
export function NotificationList({ tab, q, dateFrom, dateTo, onItemClick }: NotificationListProps) {
  // Normalise to the generated query params — omit the no-op ALL tab + empty
  // search/date bounds so the query key (and the wire request) stay clean.
  // `dateFrom`/`dateTo` are the hand-typed ARC-111 extension (see list-params.ts).
  const params: NotificationsListParamsExt = {
    limit: 50,
    ...(tab !== 'ALL' ? { tab } : {}),
    ...(q?.trim() ? { q: q.trim() } : {}),
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
  };
  const { data, isLoading } = useNotificationsList(params);
  const openDetail = useOpenNotificationDetail();
  const { markRead } = useNotificationActions();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4" data-testid="notification-list-loading">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Skeleton className="size-10 shrink-0 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-3.5 w-2/3 rounded" />
              <Skeleton className="h-3 w-1/3 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    const message = q?.trim() ? 'No notifications match your search.' : 'No notifications';
    return (
      <div className="flex flex-col items-center justify-center gap-1 py-12 text-center" data-testid="notification-empty">
        <p className="text-base font-semibold text-primary">{message}</p>
        {!q?.trim() && <p className="text-sm text-muted">We&apos;ll let you know when something happens.</p>}
      </div>
    );
  }

  const open = (notification: NotificationResponseDto) => {
    markRead(notification);
    openDetail(notification);
    onItemClick?.();
  };

  const buckets = groupByRecency(items);

  return (
    <div className="flex flex-col gap-4 px-4 pb-4" data-testid="notification-list">
      {buckets.map((bucket) => (
        <section key={bucket.key}>
          <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-muted">{bucket.label}</h3>
          <ul className="flex flex-col gap-2">
            {groupByMarket(bucket.items).map((entry) =>
              entry.kind === 'thread' ? (
                <NotificationThread key={`market:${entry.marketId}`} thread={entry} onItemClick={open} />
              ) : (
                <NotificationItem key={entry.item.id} notification={entry.item} onClick={() => open(entry.item)} />
              ),
            )}
          </ul>
        </section>
      ))}
    </div>
  );
}
